// /admin/support/categories — admin-only CRUD for ticket categories.

import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../../api/client';
import type { AdminSupportCategory, TicketPriority } from '../../types';

const PRIORITIES: TicketPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];

interface Form {
  id?: string;
  name: string;
  description: string;
  sortOrder: number;
  defaultPriority: TicketPriority;
  autoAssignToId: string;
  isActive: boolean;
}
const EMPTY: Form = { name: '', description: '', sortOrder: 0, defaultPriority: 'MEDIUM', autoAssignToId: '', isActive: true };

interface StaffOption { id: string; name: string; email: string; role: 'HELPDESK' | 'ADMIN' }

export function SupportCategoriesPage() {
  const [rows, setRows] = useState<AdminSupportCategory[] | null>(null);
  const [staff, setStaff] = useState<StaffOption[]>([]);
  const [editing, setEditing] = useState<Form | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function reload() {
    try {
      const [list, s] = await Promise.all([
        api<AdminSupportCategory[]>('/api/support/categories/_admin'),
        api<StaffOption[]>('/api/support/tickets/_helpers/staff'),
      ]);
      setRows(list);
      setStaff(s);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Load failed');
    }
  }
  useEffect(() => { void reload(); }, []);

  function startEdit(c: AdminSupportCategory) {
    setEditing({
      id: c.id,
      name: c.name,
      description: c.description ?? '',
      sortOrder: c.sortOrder,
      defaultPriority: c.defaultPriority,
      autoAssignToId: c.autoAssignToId ?? '',
      isActive: c.isActive,
    });
  }

  async function onSave(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!editing) return;
    setSaving(true);
    try {
      const body = JSON.stringify({
        name: editing.name,
        description: editing.description,
        sortOrder: editing.sortOrder,
        defaultPriority: editing.defaultPriority,
        autoAssignToId: editing.autoAssignToId || null,
        isActive: editing.isActive,
      });
      if (editing.id) {
        await api(`/api/support/categories/${editing.id}`, { method: 'PUT', body });
      } else {
        await api('/api/support/categories', { method: 'POST', body });
      }
      setEditing(null);
      await reload();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Save failed');
    } finally { setSaving(false); }
  }

  async function onDelete(c: AdminSupportCategory) {
    if (c.ticketCount > 0) {
      alert(`This category has ${c.ticketCount} tickets. Reassign them first.`);
      return;
    }
    if (!confirm(`Delete category "${c.name}"?`)) return;
    try { await api(`/api/support/categories/${c.id}`, { method: 'DELETE' }); await reload(); }
    catch (err) { alert(err instanceof Error ? err.message : 'Failed'); }
  }

  return (
    <section className="admin-page">
      <header className="admin-page-header">
        <h1>Support categories</h1>
        <button type="button" className="btn-primary" onClick={() => setEditing({ ...EMPTY })}>New category</button>
      </header>
      {error && <div className="form-error">{error}</div>}

      {!rows && !error && <p style={{ color: 'var(--text-muted)' }}>Loading…</p>}
      {rows && (
        <table className="admin-table">
          <thead>
            <tr><th>Name</th><th>Default priority</th><th>Sort</th><th>Auto-assign</th><th>Active</th><th>Tickets</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td>{c.defaultPriority.toLowerCase()}</td>
                <td>{c.sortOrder}</td>
                <td>{c.autoAssignTo?.name ?? '—'}</td>
                <td>{c.isActive ? '✓' : '—'}</td>
                <td>{c.ticketCount}</td>
                <td className="admin-table-actions">
                  <button type="button" className="btn-link" onClick={() => startEdit(c)}>Edit</button>
                  <button type="button" className="btn-link" onClick={() => void onDelete(c)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <form onSubmit={(e) => void onSave(e)} className="admin-form" style={{ marginTop: 16 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 8px' }}>{editing.id ? 'Edit' : 'New'} category</h2>
          <div className="form-group">
            <label>Name</label>
            <input type="text" required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} disabled={saving} />
          </div>
          <div className="form-group">
            <label>Description (optional)</label>
            <input type="text" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} disabled={saving} />
          </div>
          <div className="form-row-pair">
            <div className="form-group">
              <label>Default priority</label>
              <select className="admin-select" value={editing.defaultPriority} onChange={(e) => setEditing({ ...editing, defaultPriority: e.target.value as TicketPriority })} disabled={saving}>
                {PRIORITIES.map((p) => (<option key={p} value={p}>{p.toLowerCase()}</option>))}
              </select>
            </div>
            <div className="form-group">
              <label>Sort order</label>
              <input type="number" value={editing.sortOrder} onChange={(e) => setEditing({ ...editing, sortOrder: Number(e.target.value) })} disabled={saving} />
            </div>
          </div>
          <div className="form-group">
            <label>Auto-assign new tickets to</label>
            <select className="admin-select" value={editing.autoAssignToId} onChange={(e) => setEditing({ ...editing, autoAssignToId: e.target.value })} disabled={saving}>
              <option value="">— no auto-assignment —</option>
              {staff.map((s) => (<option key={s.id} value={s.id}>{s.name} ({s.role})</option>))}
            </select>
          </div>
          <label className="form-checkbox">
            <input type="checkbox" checked={editing.isActive} onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })} disabled={saving} />
            Active (shown in the new-ticket dropdown)
          </label>
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      )}
    </section>
  );
}
